import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { userService } from '../_services';
import PasswordStrengthMeter, {
    isPasswordStrong,
    PASSWORD_NOT_STRONG_ENOUGH,
    passwordHasDisallowedWhitespace,
    PASSWORD_SPACE_NOT_ALLOWED,
} from '@/components/ui/PasswordStrengthMeter';
import { buildPasswordPolicyContext, getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';

function ConfirmDeletePopup({
    titleText,
    ConfirmSection,
    confirmSection,
    removeFilter,
    closePopup,
    openManageUser,
    manageUserName,
    manageUserEmail,
    manageUserId,
    manageUserPagin,
    manageUserSearch,
    manageUserPassword,
    EditUserSection,
    UserName: initialUserName,
    UserEmail: initialUserEmail,
    UserId,
    handleEditUser,
    CloseEditUserPopup
}) {
    const [loading, setLoading] = useState(false);
    const [userName, setUserName] = useState(initialUserName || '');
    const [userEmail, setUserEmail] = useState(initialUserEmail || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [showConfirmEdit, setShowConfirmEdit] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const passwordPolicyContext = useMemo(
        () => buildPasswordPolicyContext({ email: userEmail, username: userName, fullName: userName || '' }),
        [userEmail, userName]
    );

    const validateEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleResetPassword = useCallback(() => {
        const newErrors = {};
        
        if (!password) {
            newErrors.password = 'New password is required';
        } else if (userService.ValidateSpace(password)) {
            newErrors.password = 'Space not acceptable';
        } else if (passwordHasDisallowedWhitespace(password)) {
            newErrors.password = PASSWORD_SPACE_NOT_ALLOWED;
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!isPasswordStrong(password, passwordPolicyContext)) {
            newErrors.password =
                getPasswordPolicyViolationMessage(password, passwordPolicyContext) || PASSWORD_NOT_STRONG_ENOUGH;
        }
        
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm new password is required';
        } else if (confirmPassword.length < 8) {
            newErrors.confirmPassword = 'New Password and Confirm Password must match';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'New Password and Confirm Password must match';
        }
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length === 0) {
            setLoading(true);
            closePopup?.();
            openManageUser?.(manageUserName, manageUserEmail, manageUserId, titleText, confirmSection, manageUserPagin, manageUserSearch, [password, confirmPassword]);
        }
    }, [password, confirmPassword, closePopup, openManageUser, manageUserName, manageUserEmail, manageUserId, titleText, confirmSection, manageUserPagin, manageUserSearch, passwordPolicyContext]);

    const handleEditSubmit = useCallback(() => {
        const newErrors = {};
        
        if (!userName) {
            newErrors.userName = 'User Name cannot be blank';
        }
        
        if (!userEmail) {
            newErrors.userEmail = 'Enter a valid email ID';
        } else if (!validateEmail(userEmail)) {
            newErrors.userEmail = 'Enter a valid email ID';
        }
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length === 0) {
            setShowConfirmEdit(true);
        }
    }, [userName, userEmail]);

    const handleConfirmEdit = useCallback(() => {
        setLoading(true);
        handleEditUser?.([UserId, userName, userEmail]);
    }, [UserId, userName, userEmail, handleEditUser]);

    const handleBack = useCallback(() => {
        closePopup?.();
        openManageUser?.('', '', '', '', '', manageUserPagin, manageUserSearch, manageUserPassword);
    }, [closePopup, openManageUser, manageUserPagin, manageUserSearch, manageUserPassword]);

    const managePositive = useCallback(() => {
        closePopup?.();
        openManageUser?.(manageUserName, manageUserEmail, manageUserId, titleText, confirmSection, manageUserPagin, manageUserSearch, [password, confirmPassword]);
    }, [closePopup, openManageUser, manageUserName, manageUserEmail, manageUserId, titleText, confirmSection, manageUserPagin, manageUserSearch, password, confirmPassword]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            if (ConfirmSection) {
                closePopup?.();
            } else {
                handleBack();
            }
        }
    }, [ConfirmSection, closePopup, handleBack]);

    // Render confirmation dialog for edit
    if (showConfirmEdit) {
        return (
            <Dialog open={true} onOpenChange={() => setShowConfirmEdit(false)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Confirm Changes</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-6 py-4 text-center">
                        <p className="text-[15px] text-muted-foreground max-w-[300px]">Are you sure you want to update this user?</p>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setShowConfirmEdit(false)}>Cancel</Button>
                        <Button onClick={handleConfirmEdit} disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader className={confirmSection === 'ResendPasswordSection' ? 'text-left' : 'text-center pb-0'}>
                    <DialogTitle className={
                        (ConfirmSection || confirmSection === 'DeleteSection')
                            ? 'sr-only'
                            : confirmSection === 'ResendPasswordSection'
                                ? 'text-left'
                                : 'text-center'
                    }>
                        {titleText}
                    </DialogTitle>
                </DialogHeader>
                
                {/* Simple Confirm Section */}
                {ConfirmSection && (
                    <>
                        <div className="flex flex-col items-center text-center gap-4 mt-4 px-6 py-4">
                            <AlertTriangle className="h-12 w-12 text-amber-500" />
                            <p className="text-lg font-medium text-foreground">{titleText}</p>
                        </div>
                        <DialogFooter>
                            <Button variant="secondary" onClick={closePopup}>No</Button>
                            <Button variant="destructive" onClick={removeFilter}>Yes</Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Delete Section */}
                {confirmSection === 'DeleteSection' && (
                    <>
                        <div className="flex flex-col items-center text-center gap-4 mt-4 px-6 py-4">
                            <AlertTriangle className="h-12 w-12 text-amber-500" />
                            <p className="text-lg font-medium text-foreground">{titleText}</p>
                        </div>
                        <DialogFooter>
                            <Button variant="secondary" onClick={handleBack}>No</Button>
                            <Button variant="destructive" onClick={managePositive} disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Yes
                            </Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Dashboard Section */}
                {confirmSection === 'DashboardSection' && (
                    <>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col items-center gap-6 py-4 text-center">
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="secondary" onClick={handleBack}>Cancel</Button>
                            <Button onClick={managePositive} disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Confirm
                            </Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Resend Password Section */}
                {confirmSection === 'ResendPasswordSection' && (
                    <>
                        <form id="resend-password-form" onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }} className="space-y-4 text-left">
                            <div className="space-y-2">
                                <Label htmlFor="password">New Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: '' })); }}
                                        autoComplete="new-password"
                                        className={`pr-10${errors.password ? ' border-destructive' : ''}`}
                                    />
                                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <PasswordStrengthMeter password={password} policyContext={passwordPolicyContext} />
                                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                <div className="relative">
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                                        autoComplete="new-password"
                                        className={`pr-10${errors.confirmPassword ? ' border-destructive' : ''}`}
                                    />
                                    <button type="button" tabIndex={-1} onClick={() => setShowConfirmPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                            </div>
                        </form>
                        <DialogFooter className="gap-2 px-0 pb-0 sm:px-0">
                            <Button type="submit" form="resend-password-form" disabled={loading} className="h-10">
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleBack} className="h-10">Cancel</Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Edit User Section */}
                {EditUserSection && (
                    <>
                        <div className="flex flex-col gap-4">
                            <form id="edit-user-form" onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }} className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="userName">User Name</Label>
                                    <Input
                                        id="userName"
                                        type="text"
                                        value={userName}
                                        onChange={(e) => { setUserName(e.target.value); setErrors(prev => ({ ...prev, userName: '' })); }}
                                        className={errors.userName ? 'border-destructive' : ''}
                                    />
                                    {errors.userName && <p className="text-sm text-destructive">{errors.userName}</p>}
                                </div>
                                
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="userEmail">Email</Label>
                                    <Input
                                        id="userEmail"
                                        type="email"
                                        value={userEmail}
                                        onChange={(e) => { setUserEmail(e.target.value); setErrors(prev => ({ ...prev, userEmail: '' })); }}
                                        className={errors.userEmail ? 'border-destructive' : ''}
                                    />
                                    {errors.userEmail && <p className="text-sm text-destructive">{errors.userEmail}</p>}
                                </div>
                            </form>
                        </div>
                        <DialogFooter>
                            <Button type="submit" form="edit-user-form" disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={CloseEditUserPopup}>Cancel</Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ConfirmDeletePopup;
